/**
 * Represents a Logger utility class.
 */
class Logger {
  /**
   * Indicates whether the logger is visible or not.
   * @type {boolean}
   */
  static isvicible = process.env.NODE_ENV === 'development' ? true : false;
  trace = new Error();
  /**
   * Constructs a new Logger instance.
   */
  constructor() {
    /**
     * The array to store logs.
     * @type {Array}
     */
    this.logs = [];
  }

  /**
   * Toggles the visibility of the logger.
   */
  static toggleVisibilty() {
    this.isvicible = !this.isvicible;
  }

  /**
   * Logs the provided arguments to the console.
   * @param {...any} args - args[0] must be a type of console.
   * For example: Logger.log(table, 'Hello World');
   */
  static other() {
    const args = Array.from(arguments);
    const type = args[0];
    args.shift();

    if (this.isvicible) {
      for (let i = 0; i < args.length; i++) {
        console[type](args[i]);
      }
    }
  }

  /**
   * Logs the provided arguments to the console using `console.log`.
   * @param {...any} args - The arguments to be logged.
   */
  static log() {
    const args = Array.from(arguments);
    if (this.isvicible) {
      // for (let i = 0; i < args.length; i++) {
      //   console.log(args[i]);
      // }
      console.log(...args);
    }
  }

  /**
   * Logs the provided arguments to the console using `console.error`.
   * @param {...any} args - The arguments to be logged.
   */
  static error() {
    const args = Array.from(arguments);
    if (this.isvicible) {
      for (let i = 0; i < args.length; i++) {
        console.error(args[i]);
      }
    }
  }

  /**
   * Logs the provided arguments to the console using `console.info`.
   * @param {...any} args - The arguments to be logged.
   */
  static info() {
    const args = Array.from(arguments);
    if (this.isvicible) {
      for (let i = 0; i < args.length; i++) {
        console.info(args[i]);
      }
    }
  }

  /**
   * Logs the provided arguments to the console using `console.warn`.
   * @param {...any} args - The arguments to be logged.
   */
  static warn() {
    const args = Array.from(arguments);
    if (this.isvicible) {
      for (let i = 0; i < args.length; i++) {
        console.warn(args[i]);
      }
    }
  }

  /**
   * Clears the logs array.
   */
  static clear() {
    this.logs = [];
  }

  /**
   * Returns the logs array.
   * @returns {Array} The logs array.
   */
  static getLogs() {
    return this.logs;
  }
}

/**
 * Represents a Message object.
 */
class Message {
  /**
   * Constructs a new Message instance.
   * @param {string} message - The message content.
   * @param {string} type - The message type.
   * @param {string} uniqueId - The unique identifier of the message.
   */
  constructor(message, type, uniqueId) {
    /**
     * The message content.
     * @type {string}
     */
    this.message = message;

    /**
     * The message type.
     * @type {string}
     */
    this.type = type;

    /**
     * The unique identifier of the message.
     * @type {string}
     */
    this.uniqueId = uniqueId;
  }

  /**
   * Throws an error when attempting to set the message property.
   * @param {string} value - The value to set.
   * @throws {Error} The message is a read-only property.
   */
  set message(value) {
    throw new Error('message is a read-only property');
  }

  /**
   * Throws an error when attempting to set the type property.
   * @param {string} value - The value to set.
   * @throws {Error} The type is a read-only property.
   */
  set type(value) {
    throw new Error('type is a read-only property');
  }

  /**
   * Throws an error when attempting to set the uniqueId property.
   * @param {string} value - The value to set.
   * @throws {Error} The uniqueId is a read-only property.
   */
  set uniqueId(value) {
    throw new Error('uniqueId is a read-only property');
  }

  /**
   * Returns the message content.
   * @returns {string} The message content.
   */
  get message() {
    return this.message;
  }

  /**
   * Returns the message type.
   * @returns {string} The message type.
   */
  get type() {
    return this.type;
  }

  /**
   * Returns the unique identifier of the message.
   * @returns {string} The unique identifier of the message.
   */
  get uniqueId() {
    return this.uniqueId;
  }
}

window.Logger = Logger;
